import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import { usePosts } from '../../hooks/useSupabaseContent';
import { useCanonicalUrl } from '../../hooks/useCanonicalUrl';
import { createPostSEO, createNotFoundSEO } from '../../lib/seo';
import LoadingSpinner from '../ui/LoadingSpinner';
import ImagePreview from '../ui/ImagePreview';
import { useImagePreview } from '../../hooks/useImagePreview';
import Breadcrumbs, { BreadcrumbItem } from '../layout/Breadcrumbs';
import '../../styles/components/post-detail.css';
import '../../styles/utilities/spacing.css';
import '../../styles/utilities/typography.css';
import '../../styles/utilities/buttons.css';
import '../../styles/utilities/layout.css';

const formatPostDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const PostDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: posts, loading } = usePosts();
  const { previewImage, openPreview, closePreview, isOpen } = useImagePreview();

  // Add click handlers to inline images after content is rendered
  React.useEffect(() => {
    const handleInlineImageClick = (event: Event) => {
      const target = event.target as HTMLImageElement;
      if (target.classList.contains('clickable-image')) {
        const src = target.getAttribute('data-preview-src') || target.src;
        const alt = target.getAttribute('data-preview-alt') || target.alt;
        openPreview(src, alt);
      }
    };

    document.addEventListener('click', handleInlineImageClick);
    return () => document.removeEventListener('click', handleInlineImageClick);
  }, [openPreview]);

  // Find the post by slug (extracted from route) - only show published posts
  // Note: public_visibility only controls grid display, not URL accessibility
  const post = posts?.find(p => {
    if (!p.route || !p.is_published) return false;
    // Extract slug from route (e.g., "/post/welcome" -> "welcome")
    const routeSlug = p.route.split('/').pop();
    return routeSlug === slug;
  });

  // Debug logging to help troubleshoot visibility issues
  React.useEffect(() => {
    if (posts && slug) {
      console.log('PostDetail Debug Info:');
      console.log('- Looking for slug:', slug);
      console.log('- Available posts:', posts.map(p => ({
        title: p.title,
        route: p.route,
        slug: p.route?.split('/').pop(),
        is_published: p.is_published,
        public_visibility: p.public_visibility
      })));
      console.log('- Found post:', post ? {
        title: post.title,
        is_published: post.is_published,
        public_visibility: post.public_visibility
      } : 'NOT FOUND');
    }
  }, [posts, slug, post]);

  // Set canonical URL - provide fallback slug for immediate SEO setup
  const canonicalConfig = React.useMemo(() => {
    if (post) {
      return createPostSEO(post, slug);
    }
    return createNotFoundSEO(slug!);
  }, [post, slug]);

  useCanonicalUrl(canonicalConfig);

  // Create breadcrumbs for the post
  const breadcrumbs = React.useMemo(() => {
    const items: BreadcrumbItem[] = [
      { label: 'Home', path: '/' },
    ];
    
    if (post) {
      // Check if user came from a category page
      let cameFromCategory = false;
      
      // Check if navigation state indicates coming from category
      if (location.state && location.state.fromCategory) {
        cameFromCategory = true;
      } else {
        // Fallback: check document.referrer
        const referrer = document.referrer;
        if (referrer && post.category && referrer.includes(`/${post.category}`)) {
          cameFromCategory = true;
        }
      }
      
      // Add category breadcrumb only if user came from category page AND it's not 'general'
      if (post.category && post.category !== 'general' && cameFromCategory) {
        const categoryLabel = post.category.charAt(0).toUpperCase() + post.category.slice(1);
        items.push({ 
          label: categoryLabel, 
          path: `/${post.category}` 
        });
      }
      
      // Add the post title as the final active item
      items.push({ label: post.title, isActive: true });
    } else {
      items.push({ label: 'Post Not Found', isActive: true });
    }
    
    return items;
  }, [post, location.state]);

  // Show loading when we don't have any posts data yet (first visit with no cache)
  if (loading && !posts) {
    return (
      <div className="page-container">
        <main className="page-main">
          <LoadingSpinner size="large" message="Loading post..." />
        </main>
      </div>
    );
  }

  // Enhanced function to preserve multiple line breaks and spacing
  const preprocessContent = (content: string) => {
    if (!content) return '';
    
    let processed = content;
    
    // Handle multiple consecutive newlines (3 or more)
    // Convert them to standalone HTML breaks that won't interfere with markdown parsing
    processed = processed.replace(/\n{3,}/g, (match) => {
      const extraBreaks = match.length - 2;
      // Use standalone br tags with newlines to maintain markdown flow
      return '\n\n' + '<br/>'.repeat(extraBreaks) + '\n\n';
    });
    
    // Parse breakout directive with optional height from HTML comments
    // Pattern: ![](url)<!-- breakout:75% --> or ![](url)<!-- breakout -->
    processed = processed.replace(/!\[([^\]]*)\]\(([^)]+)\)<!--\s*breakout(?::(\d+)%)?\s*-->/g, 
      (match, alt, src, height) => {
        const heightClass = height ? ` breakout-height-${height}` : '';
        return `<img src="${src}" alt="${alt}" class="markdown-image-breakout${heightClass} clickable-image" data-preview-src="${src}" data-preview-alt="${alt}" data-height="${height || '60'}" />`;
      }
    );
    
    // Parse full-width directive from HTML comments
    // Pattern: ![](url)<!-- full-width -->
    processed = processed.replace(/!\[([^\]]*)\]\(([^)]+)\)<!--\s*full-width\s*-->/g, 
      (match, alt, src) => {
        return `<img src="${src}" alt="${alt}" class="markdown-image-full-width clickable-image" data-preview-src="${src}" data-preview-alt="${alt}" />`;
      }
    );
    
    // Parse width attributes from HTML comments and convert to inline styles (legacy support)
    // Pattern: ![](url)<!-- {"width":330} -->
    processed = processed.replace(/!\[([^\]]*)\]\(([^)]+)\)<!--\s*\{[^}]*"width"\s*:\s*(\d+)[^}]*\}\s*-->/g, 
      (match, alt, src, width) => {
        return `<img src="${src}" alt="${alt}" style="width: ${width}px; height: auto;" class="markdown-image-inline clickable-image" data-preview-src="${src}" data-preview-alt="${alt}" />`;
      }
    );
    
    // Convert regular markdown images to inline images for even distribution
    // Pattern: ![](url) (without width comments)
    processed = processed.replace(/!\[([^\]]*)\]\(([^)]+)\)(?!<!--)/g, 
      (match, alt, src) => {
        return `<img src="${src}" alt="${alt}" class="markdown-image-inline clickable-image" data-preview-src="${src}" data-preview-alt="${alt}" />`;
      }
    );
    
    // Find consecutive breakout images and wrap them in a breakout container
    processed = processed.replace(/(<img[^>]*class="[^"]*markdown-image-breakout[^"]*"[^>]*\/>[\s]*)+/g, (match) => {
      // Count the number of images in this group
      const imageCount = (match.match(/<img[^>]*class="[^"]*markdown-image-breakout[^"]*"/g) || []).length;
      
      // Extract height from the first image to apply to container
      const heightMatch = match.match(/data-height="(\d+)"/);
      const height = heightMatch ? heightMatch[1] : '60';
      
      return `<div class="breakout-images-container" data-image-count="${imageCount}" data-height="${height}">${match}</div>`;
    });
    
    // Find consecutive inline images and wrap them in a container with count
    // Only group markdown-image-inline, not markdown-image-full-width or markdown-image-breakout
    processed = processed.replace(/(<img[^>]*class="[^"]*markdown-image-inline[^"]*"[^>]*\/>[\s]*)+/g, (match) => {
      // Count the number of images in this group
      const imageCount = (match.match(/<img[^>]*class="[^"]*markdown-image-inline[^"]*"/g) || []).length;
      return `<div class="inline-images-container" data-image-count="${imageCount}">${match}</div>`;
    });
    
    // Let remark-breaks handle single line breaks naturally
    // It will convert single \n to <br> tags automatically
    
    return processed;
  };

  // Custom image renderer that handles width attributes from HTML comments
  const ImageComponent = ({ src, alt, ...props }: any) => {
    return (
      <img 
        src={src} 
        alt={alt} 
        className="markdown-image clickable-image" 
        onClick={() => openPreview(src, alt)} 
        {...props} 
      />
    );
  };

  // Custom paragraph renderer that handles image rows
  const ParagraphComponent = ({ children, ...props }: any) => {
    // Check if this paragraph contains only images
    const childrenArray = React.Children.toArray(children);
    const isImageRow = childrenArray.every(child => 
      React.isValidElement(child) && child.type === 'img'
    );

    if (isImageRow) {
      return (
        <div className="image-row" {...props}>
          {children}
        </div>
      );
    }

    return <p className="markdown-p-large" {...props}>{children}</p>;
  };

  if (!post) {
    return (
      <div className="page-container">
        <div className="content-section">
          <h1>Post Not Found</h1>
          <p>The post you're looking for doesn't exist or has been removed.</p>
          <button 
            onClick={() => navigate('/')}
            className="btn btn-primary mt-4"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* React 19 Native Metadata Approach - Alternative to useCanonicalUrl hook */}
      {/* Uncomment to use React 19's direct JSX metadata instead of the hook approach:
      {post && (
        <CanonicalLink 
          href={getCanonicalUrl(post.route)}
          title={`${post.title} | LUT Rolls Blog`}
          description={post.description}
        />
      )}
      */}
      
      <main className="page-main">
        <div className="page-content">
          <Breadcrumbs items={breadcrumbs} />
          
          <article className="post-detail">
            <header className="page-header">
              <h1>{post.title}</h1>
              <div className="page-meta">
                <small>Published on {formatPostDate(post.created_at)}</small>
              </div>
            </header>

            <div className="post-content">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm, remarkBreaks]}
                rehypePlugins={[rehypeRaw]}
                components={{
                  // Use slightly larger headings for full page display
                  h1: ({children}) => <h1 className="markdown-h1-large">{children}</h1>,
                  h2: ({children}) => <h2 className="markdown-h2-large">{children}</h2>,
                  h3: ({children}) => <h3 className="markdown-h3-large">{children}</h3>,
                  p: ParagraphComponent,
                  img: ImageComponent,
                  ul: ({children}) => <ul className="markdown-ul">{children}</ul>,
                  ol: ({children}) => <ol className="markdown-ol">{children}</ol>,
                  li: ({children}) => <li className="markdown-li">{children}</li>,
                  strong: ({children}) => <strong className="markdown-strong">{children}</strong>,
                  em: ({children}) => <em className="markdown-em">{children}</em>,
                  code: ({children}) => <code className="markdown-code">{children}</code>,
                  blockquote: ({children}) => <blockquote className="markdown-blockquote">{children}</blockquote>,
                  // Handle line breaks properly
                  br: () => <br className="markdown-break" />,
                }}
              >
                {preprocessContent(post.content)}
              </ReactMarkdown>
            </div>
          </article>
        </div>
      </main>

      {/* Image Preview Modal */}
      {previewImage && (
        <ImagePreview
          src={previewImage.src}
          alt={previewImage.alt}
          isOpen={isOpen}
          onClose={closePreview}
        />
      )}
    </div>
  );
};

export default PostDetail; 